import { useEffect, useState } from 'react';
import { ThumbsUpIcon, ThumbsDownIcon } from '@/components/icons';
import { getLabel } from '@/i18n/maps';
import { useChatContext } from './ChatContext';
import type { FeedbackRating } from '@/api/feedback';

export interface FeedbackButtonsProps {
  /**
   * The backend's message id (from ChatResponse.assistant_message.message_id).
   * Feedback endpoints use this — NOT the client-generated ChatMessage.id.
   */
  messageBackendId: string;
}

/**
 * Thumb up / thumb down for a single assistant message.
 *
 * Owns its own state (initial GET on mount + optimistic updates on click).
 * When the user clicks 👎, this fires the "no-reason down" PUT immediately
 * so the dislike is recorded before the reason dialog even opens — matching
 * the spec's two-step interaction. The dialog is orchestrated by
 * ChatContext.openFeedbackDialog; it later fires another PUT that updates
 * the same feedback record with the reason + comment.
 *
 * Renders nothing when the chat has no feedbackApi wired (e.g. during a
 * pre-auth grace period or if the host explicitly disables feedback).
 */
export function FeedbackButtons({ messageBackendId }: FeedbackButtonsProps): JSX.Element | null {
  const { feedbackApi, sessionId, locale, openFeedbackDialog, enqueueFeedbackWrite } =
    useChatContext();

  const [rating, setRating] = useState<FeedbackRating | null>(null);

  // Initial GET — hydrate button state from server on mount / when the
  // message id or session flips. Silent fail: buttons just start unset.
  useEffect(() => {
    if (!feedbackApi || !sessionId) return;
    const ctrl = new AbortController();

    feedbackApi
      .get(messageBackendId, sessionId, { signal: ctrl.signal })
      .then((fb) => {
        if (ctrl.signal.aborted) return;
        setRating(fb?.rating ?? null);
      })
      .catch(() => {
        // ignore — leave rating unset
      });

    return () => ctrl.abort();
  }, [feedbackApi, sessionId, messageBackendId]);

  if (!feedbackApi || !sessionId) return null;

  function toggle(next: FeedbackRating) {
    const prev = rating;
    const enqueue = enqueueFeedbackWrite;
    if (!enqueue) return;

    // Re-click on the currently active button → clear
    if (rating === next) {
      setRating(null); // optimistic — no button-disable, no visible wait
      enqueue(messageBackendId, () => feedbackApi!.clear(messageBackendId, sessionId!)).catch(() =>
        setRating(prev),
      );
      return;
    }

    setRating(next); // optimistic
    // For 'down', open the dialog IMMEDIATELY. The initial "no-reason
    // down" PUT runs in the background via the shared per-message queue;
    // the dialog's submit enqueues on the same queue so the reason PUT
    // always lands AFTER this one — even if the user is very fast.
    if (next === 'down') {
      openFeedbackDialog?.(messageBackendId);
    }
    enqueue(messageBackendId, () =>
      feedbackApi!.submit(messageBackendId, { session_id: sessionId!, rating: next }),
    ).catch(() => setRating(prev));
  }

  const upLabel = getLabel('feedback_helpful', locale);
  const downLabel = getLabel('feedback_not_helpful', locale);

  return (
    <div className="tw-flex tw-items-center tw-gap-1 tw-pt-1">
      <ThumbButton
        aria-label={upLabel}
        active={rating === 'up'}
        onClick={() => toggle('up')}
      >
        <ThumbsUpIcon width={14} height={14} />
      </ThumbButton>
      <ThumbButton
        aria-label={downLabel}
        active={rating === 'down'}
        onClick={() => toggle('down')}
      >
        <ThumbsDownIcon width={14} height={14} />
      </ThumbButton>
    </div>
  );
}

function ThumbButton({
  active,
  onClick,
  children,
  ...rest
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  'aria-label': string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-md tw-transition-colors ${
        active
          ? 'tw-bg-cal-green-100 tw-text-cal-green-700'
          : 'tw-text-cal-ink-muted hover:tw-bg-cal-bg-soft hover:tw-text-cal-ink'
      }`}
      {...rest}
    >
      {children}
    </button>
  );
}
